import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../lib/cn.js";
import { Spinner } from "./Spinner.js";

const buttonStyles = cva(
  [
    "inline-flex items-center justify-center gap-2 font-medium",
    "rounded-md border transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "cursor-pointer",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-accent text-accent-fg border-transparent hover:bg-accent-deep",
        secondary:
          "bg-elevated text-fg border-line hover:bg-subtle",
        ghost:
          "bg-transparent text-fg border-transparent hover:bg-subtle",
        danger:
          "bg-danger text-white border-transparent hover:opacity-90",
        link:
          "bg-transparent text-accent border-transparent underline-offset-2 hover:underline px-0 h-auto",
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-11 px-5 text-base",
        lg: "h-12 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

type ButtonOwnProps = {
  readonly loading?: boolean;
  readonly leadingIcon?: ReactNode;
  readonly trailingIcon?: ReactNode;
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonStyles> &
  ButtonOwnProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      loading,
      leadingIcon,
      trailingIcon,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled === true || loading === true}
        className={cn(buttonStyles({ variant, size }), className)}
        {...rest}
      >
        {loading === true ? (
          <Spinner size={size === "lg" ? "md" : "sm"} />
        ) : (
          leadingIcon
        )}
        {children}
        {loading !== true ? trailingIcon : null}
      </button>
    );
  },
);
